/**
 * PSP/CRE Tool v2.0 - Operations Configuration Constants
 * 
 * Purpose: Define operation types, postures, engagement levels, terrain,
 * weather, and threat configurations with full calculator integration.
 * 
 * Author: Dr. Tatar Radu MD
 * Date: December 21, 2024
 * Version: 2.2 - EXT-06: Add first_aid_delay_minutes to THREAT_LEVELS, add CLS_TRAINING_QUALITY (February 22, 2026)
 *
 * VERSION 2.2 CHANGES (February 22, 2026) - EXT-06:
 * - ADD: `first_aid_delay_minutes` field inside each THREAT_LEVELS[x].medical sub-object.
 *   Represents time (minutes) before CLS can safely access a wounded soldier under each
 *   threat condition. survivalModule.js SVM-06 replaces its local `threatDelays` object
 *   by reading THREAT_LEVELS[threat].medical.first_aid_delay_minutes.
 *   Values: LOW_THREAT=2, MODERATE_THREAT=5, HIGH_THREAT=10, EXTREME_THREAT=20.
 * - ADD: `CLS_TRAINING_QUALITY` new export constant. Four training levels (UNTRAINED,
 *   BASIC, STANDARD, ADVANCED) with survival_modifier and txa_probability fields.
 *   survivalModule.js SVM-02 will delete its local CLS_TRAINING_QUALITY block and import
 *   from here. Values derived from TCCC efficacy research and Ukraine 2022-2024 outcomes.
 * - Removed: Institution Romanian Military Medical Institute (APP_RULES 5.3).
 * 
 * BUG-001 FIX (January 18, 2026):
 * - Changed all "modifier:" property names to "casualty_multiplier:"
 * - Changed all ".modifier" property accesses to ".casualty_multiplier"
 * - battleCasualtyModule.js expects ".casualty_multiplier" property
 * - Without this fix, all modifiers returned undefined → NaN calculations
 * - Total changes: 45 property definitions + 16 property accesses
 * 
 * References:
 * - NATO AJMedP-1 Allied Joint Medical Support Doctrine
 * - NATO ATP 3-90 Offense and Defense
 * - FM 4-02 Army Health System
 * - Ukraine 2022-2024 combat data (ISW, RUSI, Oryx)
 * - Romanian Army tactical doctrine
 * 
 * Key Enhancements v2.0:
 * - All icons imported from icons.js (no hardcoding)
 * - Ukraine 2022-2024 validated multipliers
 * - Drone warfare integration
 * - Enhanced medical planning factors
 * - Calculator integration functions
 * 
 * Usage:
 * import { 
 *   OPERATION_TYPES,
 *   OPERATIONAL_POSTURES,
 *   calculateCombinedModifier,
 *   determineEvacuationParadigm
 * } from '../constants/operationsConfig';
 */

import { ICONS, TACTICAL_ICONS, STATUS_ICONS, WEATHER_ICONS } from '../utils/icons';

// ============================================================================
// OPERATION TYPES
// ============================================================================

/**
 * OPERATION_TYPES - Primary operation classifications
 * 
 * Modifiers based on historical casualty analysis and Ukraine 2022-2024:
 * - River crossings: 1.5x (Siverskyi Donets disaster validation)
 * - Amphibious assault: 1.6x (historical - limited Ukraine data)
 * - Urban combat: 1.4x (Bakhmut, Mariupol, Severodonetsk)
 * - Airborne assault: 1.5x (Hostomel validation - actually higher)
 * - Mountain operations: 1.3x (limited Ukraine data)
 * - Stability operations: 0.3x (peacekeeping low-intensity)
 */
export const OPERATION_TYPES = {
  GROUND_COMBAT: {
    value: 'GROUND_COMBAT',
    label: 'Ground Combat',
    icon: TACTICAL_ICONS.ATTACK,
    casualty_multiplier: 1.0,
    description: 'Standard ground combat operations',
    
    ukraine_data: {
      observed_modifier: 1.0,
      notes: 'Baseline for all Ukraine 2022-2024 calculations',
      primary_threats: ['Artillery (60-70%)', 'Drones (15-20%)', 'Small arms (10-15%)']
    },
    
    medical: {
      golden_hour_achievable: 0.60,
      typical_paradigm: 'CONTESTED',
      mascal_risk: 'MODERATE',
      primary_injury_types: ['Blast/fragment', 'Penetrating trauma', 'Burns']
    },
    
    romanian_term: 'Luptă Terestră'
  },
  
  URBAN_COMBAT: {
    value: 'URBAN_COMBAT',
    label: 'Urban Combat',
    icon: ICONS.FACILITY.BUILDING,
    casualty_multiplier: 1.4,
    description: '40% higher casualties in urban terrain',
    
    ukraine_data: {
      observed_modifier: 1.45,
      examples: ['Bakhmut', 'Mariupol', 'Severodonetsk', 'Soledar'],
      notes: 'Urban combat consistently 40-50% higher casualties',
      attacker_requirement: '3:1 minimum force ratio',
      duration_factor: 'Operations last 2-3x longer than planned',
      lessons: [
        'Building-to-building evacuation extremely difficult',
        'Sniper threat to CASEVAC constant',
        'Underground facilities critical for medical',
        'Civilian casualties complicate medical planning'
      ]
    },
    
    medical: {
      golden_hour_achievable: 0.35,
      typical_paradigm: 'CONTESTED',
      mascal_risk: 'HIGH',
      primary_injury_types: ['Blast/fragment', 'Crush injuries', 'Burns', 'Falling debris'],
      walking_blood_bank: true,
      pfc_required: true
    },
    
    romanian_term: 'Luptă în Localități'
  },
  
  RIVER_CROSSING: {
    value: 'RIVER_CROSSING',
    label: 'River Crossing',
    icon: TACTICAL_ICONS.RIVER_CROSSING,
    casualty_multiplier: 1.5,
    description: '50% higher casualties - high risk operation',
    
    ukraine_data: {
      observed_modifier: 1.8,
      examples: ['Siverskyi Donets disaster (May 2022)', 'Dnipro crossings (Kherson)'],
      notes: 'WITHOUT air superiority, crossings can be catastrophic',
      lessons: [
        'Siverskyi Donets: Russian BTG lost 70+ vehicles, 400+ casualties',
        'Concentrated forces = artillery magnet',
        'Evacuation across water extremely difficult',
        'Forward surgical teams must cross early'
      ],
      warning: 'Ukraine data suggests 1.8x or higher without air cover'
    },
    
    medical: {
      golden_hour_achievable: 0.15,
      typical_paradigm: 'DENIED',
      mascal_risk: 'EXTREME',
      medical_delay_hours: 6,
      walking_blood_bank: true,
      pfc_required: true,
      forward_surgical_required: true
    },
    
    romanian_term: 'Forțare de Cursuri de Apă'
  },
  
  MOUNTAIN_OPERATIONS: {
    value: 'MOUNTAIN_OPERATIONS',
    label: 'Mountain Operations',
    icon: WEATHER_ICONS.MOUNTAIN,
    casualty_multiplier: 1.3,
    description: '30% higher casualties in mountainous terrain',
    
    ukraine_data: {
      observed_modifier: 1.25,
      notes: 'Limited mountain combat in Ukraine - primarily Carpathian concerns',
      altitude_effects: 'Above 2000m: additional 10% DNBI',
      lessons: [
        'Helicopter evacuation critical but weather-limited',
        'Ground evacuation extremely slow',
        'Cold injuries increase significantly'
      ]
    },
    
    medical: {
      golden_hour_achievable: 0.25,
      typical_paradigm: 'CONTESTED',
      mascal_risk: 'MODERATE',
      altitude_dnbi_factor: 1.3,
      helicopter_dependency: 'HIGH',
      pfc_required: true
    },
    
    romanian_term: 'Operațiuni de Munte'
  },
  
  AMPHIBIOUS_ASSAULT: {
    value: 'AMPHIBIOUS_ASSAULT',
    label: 'Amphibious Assault',
    icon: ICONS.TRANSPORT.BOAT || '🚤',
    casualty_multiplier: 1.6,
    description: '60% higher casualties - extremely dangerous',
    
    ukraine_data: {
      observed_modifier: 'N/A',
      notes: 'No major amphibious assaults in Ukraine 2022-2024',
      historical_reference: 'D-Day, Iwo Jima data used',
      black_sea_context: 'Naval dominance prevented amphibious ops'
    },
    
    medical: {
      golden_hour_achievable: 0.10,
      typical_paradigm: 'DENIED',
      mascal_risk: 'EXTREME',
      medical_delay_hours: 12,
      walking_blood_bank: true,
      pfc_required: true,
      forward_surgical_required: true,
      ship_medical_required: true
    },
    
    romanian_term: 'Asalt Amfibiu'
  },
  
  AIRBORNE_ASSAULT: {
    value: 'AIRBORNE_ASSAULT',
    label: 'Airborne Assault',
    icon: ICONS.TRANSPORT.AIRPLANE,
    casualty_multiplier: 1.5,
    description: '50% higher casualties - high-risk insertion',
    
    ukraine_data: {
      observed_modifier: 2.5,
      examples: ['Hostomel Airport (Feb 2022)'],
      notes: 'Hostomel showed catastrophic losses without air superiority',
      lessons: [
        'VDV losses at Hostomel exceeded 50% in some units',
        'Isolated forces without resupply/evacuation',
        'Medical support extremely limited until linkup',
        'Modern AD makes airborne very risky'
      ],
      warning: 'Ukraine data suggests 2.5x or higher without air superiority'
    },
    
    medical: {
      golden_hour_achievable: 0.15,
      typical_paradigm: 'DENIED',
      mascal_risk: 'EXTREME',
      role2_delay_hours: 24,
      role2e_delay_hours: 48,
      walking_blood_bank: true,
      pfc_required: true,
      jump_surgical_teams: true
    },
    
    romanian_term: 'Asalt Aeropurtat'
  },
  
  STABILITY_OPERATIONS: {
    value: 'STABILITY_OPERATIONS',
    label: 'Stability Operations',
    icon: ICONS.STATUS.GREEN,
    casualty_multiplier: 0.3,
    description: '70% lower casualties - peacekeeping',
    
    ukraine_data: {
      observed_modifier: 'N/A',
      notes: 'No stability ops in active conflict - post-conflict planning'
    },
    
    medical: {
      golden_hour_achievable: 0.85,
      typical_paradigm: 'PERMISSIVE',
      mascal_risk: 'LOW',
      focus: 'DNBI, accidents, sporadic attacks'
    },
    
    romanian_term: 'Operațiuni de Stabilizare'
  },
  
  MARCH: {
    value: 'MARCH',
    label: 'Road March / Movement',
    icon: TACTICAL_ICONS.MARCH,
    casualty_multiplier: 0.1,
    description: '90% lower combat casualties - non-combat movement',
    
    ukraine_data: {
      observed_modifier: 0.15,
      notes: 'Road marches subject to drone/artillery ambush',
      convoy_attack_rate: '5-10% of convoys attacked in active sectors',
      lessons: [
        'Convoys are high-value drone targets',
        'Night movement reduces but doesnt eliminate risk',
        'Dispersed movement critical'
      ]
    },
    
    medical: {
      golden_hour_achievable: 0.70,
      typical_paradigm: 'PERMISSIVE',
      mascal_risk: 'LOW',
      focus: 'Vehicle accidents, DNBI, ambush response'
    },
    
    romanian_term: 'Marș'
  }
};

// ============================================================================
// OPERATIONAL POSTURES
// ============================================================================

/**
 * OPERATIONAL_POSTURES - Tactical posture during operations
 * 
 * Based on NATO doctrine and Ukraine 2022-2024:
 * - Deliberate defense with fortifications: 0.6x (Surovikin Line)
 * - Hasty attack without prep: 1.2-1.4x (early Russian losses)
 * - Exploitation: 0.8x when enemy broken (Kharkiv offensive)
 */
export const OPERATIONAL_POSTURES = {
  ATTACK: {
    value: 'ATTACK',
    label: 'Attack',
    icon: TACTICAL_ICONS.ATTACK,
    casualty_multiplier: 1.0,
    description: 'Baseline offensive operations',
    
    allowedOperations: [
      'GROUND_COMBAT', 'URBAN_COMBAT', 'RIVER_CROSSING', 
      'MOUNTAIN_OPERATIONS', 'AMPHIBIOUS_ASSAULT', 'AIRBORNE_ASSAULT'
    ],
    
    ukraine_data: {
      observed_modifier: 1.0,
      notes: 'Baseline for offensive operations'
    },
    
    medical: {
      evacuation_priority: 'URGENT',
      mtf_status: 'MOBILE',
      golden_hour_probability: 0.50
    },
    
    romanian_term: 'Atac'
  },
  
  HASTY_ATTACK: {
    value: 'HASTY_ATTACK',
    label: 'Hasty Attack',
    icon: TACTICAL_ICONS.ATTACK,
    casualty_multiplier: 1.2,
    description: '20% higher casualties - immediate attack without preparation',
    
    allowedOperations: ['GROUND_COMBAT', 'URBAN_COMBAT'],
    
    ukraine_data: {
      observed_modifier: 1.35,
      examples: ['Early Russian advances Feb-Mar 2022', 'Some Ukrainian counter-attacks'],
      notes: 'Hasty attacks without proper ISR often catastrophic',
      warning: 'Ukraine showed 35% increase, sometimes 2-3x without artillery prep'
    },
    
    medical: {
      evacuation_priority: 'URGENT',
      mtf_status: 'OPERATIONAL',
      golden_hour_probability: 0.35,
      mascal_risk: 'HIGH'
    },
    
    romanian_term: 'Atac din Mers'
  },
  
  DELIBERATE_ATTACK: {
    value: 'DELIBERATE_ATTACK',
    label: 'Deliberate Attack',
    icon: TACTICAL_ICONS.ATTACK,
    casualty_multiplier: 0.9,
    description: '10% lower casualties - well-prepared attack',

    // BUG-PHASE-001 FIX: Deliberate attack includes planned airborne and amphibious — same
    // as ATTACK. River crossing was already present. Added AIRBORNE_ASSAULT + AMPHIBIOUS_ASSAULT.
    allowedOperations: [
      'GROUND_COMBAT', 'URBAN_COMBAT', 'RIVER_CROSSING', 'MOUNTAIN_OPERATIONS',
      'AIRBORNE_ASSAULT', 'AMPHIBIOUS_ASSAULT'
    ],
    
    ukraine_data: {
      observed_modifier: 0.85,
      examples: ['Kherson counter-offensive planning phase'],
      notes: 'Proper preparation with artillery reduces casualties significantly',
      artillery_preparation_effect: 0.7
    },
    
    medical: {
      evacuation_priority: 'URGENT',
      mtf_status: 'FULLY_OPERATIONAL',
      golden_hour_probability: 0.65,
      mascal_risk: 'MODERATE'
    },
    
    romanian_term: 'Atac Deliberat'
  },
  
  EXPLOITATION: {
    value: 'EXPLOITATION',
    label: 'Exploitation',
    icon: TACTICAL_ICONS.EXPLOITATION,
    casualty_multiplier: 1.3,
    description: '30% higher casualties - rapid advance to exploit enemy weakness',
    
    allowedOperations: ['GROUND_COMBAT', 'URBAN_COMBAT'],
    
    ukraine_data: {
      observed_modifier: 0.75,
      examples: ['Kharkiv offensive September 2022'],
      notes: 'Against broken enemy, exploitation has LOW casualties',
      contradiction: 'Historical 1.3x assumes enemy resistance; Ukraine showed 0.75x against routed enemy',
      recommendation: 'Use 1.3x if enemy organized, 0.75x if enemy routed'
    },
    
    medical: {
      evacuation_priority: 'PRIORITY',
      mtf_status: 'MOBILE',
      golden_hour_probability: 0.30,
      challenge: 'Extended evacuation distances'
    },
    
    romanian_term: 'Exploatare'
  },
  
  PURSUIT: {
    value: 'PURSUIT',
    label: 'Pursuit',
    icon: ICONS.ARROW.THICK_RIGHT,
    casualty_multiplier: 1.2,
    description: '20% higher casualties - chasing retreating enemy',
    
    allowedOperations: ['GROUND_COMBAT'],
    
    ukraine_data: {
      observed_modifier: 0.65,
      examples: ['Kharkiv pursuit September 2022'],
      notes: 'Pursuit of routed enemy has very low friendly casualties',
      enemy_casualties: 'Very high - rout casualties historically 10x'
    },
    
    medical: {
      evacuation_priority: 'ROUTINE',
      mtf_status: 'MOBILE',
      golden_hour_probability: 0.25,
      focus: 'EPW medical treatment increases dramatically'
    },
    
    romanian_term: 'Urmărire'
  },
  
  DEFENSE: {
    value: 'DEFENSE',
    label: 'Defense',
    icon: TACTICAL_ICONS.DEFEND,
    casualty_multiplier: 0.7,
    description: '30% lower casualties - defensive operations',
    
    allowedOperations: ['GROUND_COMBAT', 'URBAN_COMBAT'],
    
    ukraine_data: {
      observed_modifier: 0.70,
      notes: 'Defensive positions provide significant protection'
    },
    
    medical: {
      evacuation_priority: 'PRIORITY',
      mtf_status: 'OPERATIONAL',
      golden_hour_probability: 0.70
    },
    
    romanian_term: 'Apărare'
  },
  
  HASTY_DEFENSE: {
    value: 'HASTY_DEFENSE',
    label: 'Hasty Defense',
    icon: TACTICAL_ICONS.DEFEND,
    casualty_multiplier: 0.85,
    description: '15% lower casualties - immediate defense without preparation',
    
    allowedOperations: ['GROUND_COMBAT', 'URBAN_COMBAT'],
    
    ukraine_data: {
      observed_modifier: 0.90,
      notes: 'Even hasty defense provides protection',
      examples: ['Ukrainian initial defense Feb 2022']
    },
    
    medical: {
      evacuation_priority: 'PRIORITY',
      mtf_status: 'OPERATIONAL',
      golden_hour_probability: 0.55
    },
    
    romanian_term: 'Apărare din Mers'
  },
  
  DELIBERATE_DEFENSE: {
    value: 'DELIBERATE_DEFENSE',
    label: 'Deliberate Defense',
    icon: TACTICAL_ICONS.DEFEND,
    casualty_multiplier: 0.6,
    description: '40% lower casualties - fortified defense',
    
    allowedOperations: ['GROUND_COMBAT', 'URBAN_COMBAT'],
    
    ukraine_data: {
      observed_modifier: 0.50,
      examples: ['Surovikin Line', 'Bakhmut fortifications'],
      notes: 'Fortifications reduce casualties by 50%+',
      lessons: [
        'Prepared positions dramatically reduce casualties',
        'Medical bunkers essential',
        'Covered evacuation routes critical',
        'Counter-battery radar protects medical'
      ]
    },
    
    medical: {
      evacuation_priority: 'ROUTINE',
      mtf_status: 'FULLY_OPERATIONAL',
      golden_hour_probability: 0.85,
      hardened_medical: true
    },
    
    romanian_term: 'Apărare Pregătită'
  },
  
  RETROGRADE: {
    value: 'RETROGRADE',
    label: 'Retrograde / Withdrawal',
    icon: TACTICAL_ICONS.WITHDRAWAL,
    casualty_multiplier: 1.1,
    description: '10% higher casualties - organized withdrawal',
    
    allowedOperations: ['GROUND_COMBAT'],
    
    ukraine_data: {
      observed_modifier: 1.20,
      examples: ['Some Bakhmut withdrawals', 'Lysychansk withdrawal'],
      notes: 'Withdrawal under pressure costly',
      lessons: [
        'Covered withdrawal (delay) reduces casualties',
        'Uncoordinated retreat very costly',
        'Medical assets must leap-frog'
      ]
    },
    
    medical: {
      evacuation_priority: 'URGENT',
      mtf_status: 'STRESSED',
      golden_hour_probability: 0.35,
      challenge: 'Evacuation direction issues'
    },
    
    romanian_term: 'Retragere'
  },
  
  MARCH: {
    value: 'MARCH',
    label: 'Road March',
    icon: TACTICAL_ICONS.MARCH,
    casualty_multiplier: 0.1,
    description: '90% lower casualties - non-combat movement',
    
    allowedOperations: ['MARCH'],
    
    ukraine_data: {
      observed_modifier: 0.15,
      notes: 'Road marches still subject to deep strike'
    },
    
    medical: {
      evacuation_priority: 'ROUTINE',
      mtf_status: 'MOBILE',
      golden_hour_probability: 0.70,
      note: 'MTF surgical capability 0% during movement'
    },
    
    romanian_term: 'Marș'
  }
};

// ============================================================================
// ENGAGEMENT LEVELS
// ============================================================================

/**
 * ENGAGEMENT_LEVELS - Level of unit involvement in combat
 * Determines percentage of base casualties unit actually experiences
 */
export const ENGAGEMENT_LEVELS = {
  FULL: {
    value: 'FULL',
    label: 'Full Engagement',
    icon: STATUS_ICONS.RED,
    casualty_multiplier: 1.0,
    description: '100% of calculated casualties - direct combat',
    
    unit_position: 'FEBA (Forward Edge of Battle Area)',
    medical_priority: 'HIGHEST'
  },
  
  SUPPORT: {
    value: 'SUPPORT',
    label: 'Support',
    icon: STATUS_ICONS.YELLOW,
    casualty_multiplier: 0.3,
    description: '30% of calculated casualties - indirect fire, logistics under fire',
    
    unit_position: '15-60 km from FEBA',
    medical_priority: 'MEDIUM',
    
    ukraine_data: {
      notes: 'Deep strikes increased support unit casualties',
      drone_factor: 1.2,
      artillery_factor: 1.1
    }
  },
  
  RESERVE: {
    value: 'RESERVE',
    label: 'Reserve',
    icon: STATUS_ICONS.GREEN,
    casualty_multiplier: 0.0,
    description: '0% combat casualties - held in reserve, no contact',
    
    unit_position: '60+ km from FEBA',
    medical_priority: 'ROUTINE',
    
    note: 'Zero COMBAT casualties until committed. DNBI continues.'
  },
  
  SECURITY: {
    value: 'SECURITY',
    label: 'Security',
    icon: STATUS_ICONS.YELLOW,
    casualty_multiplier: 0.1,
    description: '10% of calculated casualties - guard duty, checkpoints',
    
    unit_position: 'Variable',
    medical_priority: 'LOW'
  }
};

// ============================================================================
// TERRAIN TYPES
// ============================================================================

/**
 * TERRAIN_TYPES - Terrain effects on casualties and operations
 * Updated with drone warfare considerations
 */
export const TERRAIN_TYPES = {
  OPEN: {
    value: 'OPEN',
    label: 'Open Terrain',
    icon: ICONS.GENERAL.SQUARE_OUTLINE,
    casualty_multiplier: 1.2,
    description: '20% higher casualties - exposed to observation and fire',
    
    ukraine_data: {
      drone_effectiveness: 1.3,
      artillery_effectiveness: 1.2,
      notes: 'Very dangerous in modern warfare - drones see everything'
    },
    
    medical: {
      evacuation_speed: 1.0,
      concealment: 'NONE'
    }
  },
  
  URBAN: {
    value: 'URBAN',
    label: 'Urban',
    icon: ICONS.FACILITY.BUILDING,
    casualty_multiplier: 1.4,
    description: '40% higher casualties - close combat, limited visibility',
    
    ukraine_data: {
      drone_effectiveness: 0.7,
      defender_bonus: 0.4,
      notes: 'Buildings limit drones but close combat very costly'
    },
    
    medical: {
      evacuation_speed: 0.5,
      concealment: 'HIGH',
      challenge: 'Building-to-building evacuation'
    }
  },
  
  FOREST: {
    value: 'FOREST',
    label: 'Forest',
    icon: WEATHER_ICONS.FOREST,
    casualty_multiplier: 0.9,
    description: '10% lower casualties - concealment from observation',
    
    ukraine_data: {
      drone_effectiveness: 0.6,
      notes: 'Tree canopy provides significant drone protection'
    },
    
    medical: {
      evacuation_speed: 0.7,
      concealment: 'HIGH'
    }
  },
  
  MOUNTAIN: {
    value: 'MOUNTAIN',
    label: 'Mountain',
    icon: WEATHER_ICONS.MOUNTAIN,
    casualty_multiplier: 1.2,
    description: '20% higher casualties - difficult terrain, altitude effects',
    
    ukraine_data: {
      notes: 'Limited mountain combat in Ukraine'
    },
    
    medical: {
      evacuation_speed: 0.4,
      helicopter_dependency: 'CRITICAL',
      altitude_dnbi: 1.3
    }
  },
  
  DESERT: {
    value: 'DESERT',
    label: 'Desert',
    icon: ICONS.WEATHER.HOT || '🏜️',
    casualty_multiplier: 1.1,
    description: '10% higher casualties - heat, exposure',
    
    medical: {
      evacuation_speed: 0.8,
      heat_dnbi: 1.5,
      concealment: 'NONE'
    }
  },
  
  MIXED: {
    value: 'MIXED',
    label: 'Mixed Terrain',
    icon: ICONS.GENERAL.CIRCLE,
    casualty_multiplier: 1.0,
    description: 'Baseline - varied terrain',
    
    medical: {
      evacuation_speed: 0.8
    }
  }
};

// ============================================================================
// WEATHER CONDITIONS
// ============================================================================

/**
 * WEATHER_CONDITIONS - Weather effects on casualties and evacuation
 * Critical: Helicopter availability directly affects golden hour
 */
// CALC-011 (2026-10-09): terrain/weather applied when Tab 2 has not saved environmental factors.
// Same values Tab 2 pre-selects (Tab2_Operations.jsx initial state). Keys must exist in
// TERRAIN_TYPES / WEATHER_CONDITIONS.
export const DEFAULT_ENVIRONMENT = {
  terrain: 'MIXED',
  weather: 'CLEAR'
};

export const WEATHER_CONDITIONS = {
  CLEAR: {
    value: 'CLEAR',
    label: 'Clear',
    icon: WEATHER_ICONS.SUN,
    casualty_multiplier: 1.0,
    description: 'Baseline conditions',
    
    helicopter_availability: 0.85,
    evacuation_speed_modifier: 1.0,
    drone_effectiveness: 1.0,
    
    medical: {
      golden_hour_achievable: 'OPTIMAL',
      medevac_status: 'FULLY_OPERATIONAL'
    }
  },
  
  RAIN: {
    value: 'RAIN',
    label: 'Rain',
    icon: WEATHER_ICONS.RAIN,
    casualty_multiplier: 1.05,
    description: '5% higher casualties - reduced visibility, slippery',
    
    helicopter_availability: 0.60,
    evacuation_speed_modifier: 0.85,
    drone_effectiveness: 0.6,
    
    ukraine_data: {
      notes: 'Rain significantly degrades drone operations',
      tactical_advantage: 'Movement concealment'
    },
    
    medical: {
      golden_hour_achievable: 'DEGRADED',
      medevac_status: 'LIMITED',
      hypothermia_risk: 1.2
    }
  },
  
  SNOW: {
    value: 'SNOW',
    label: 'Snow',
    icon: WEATHER_ICONS.SNOW,
    casualty_multiplier: 1.15,
    description: '15% higher casualties - cold injuries, reduced mobility',
    
    helicopter_availability: 0.45,
    evacuation_speed_modifier: 0.70,
    drone_effectiveness: 0.7,
    
    medical: {
      golden_hour_achievable: 'SEVERELY_DEGRADED',
      medevac_status: 'SEVERELY_LIMITED',
      hypothermia_risk: 2.5,
      frostbite_risk: 1.8
    }
  },
  
  EXTREME_COLD: {
    value: 'EXTREME_COLD',
    label: 'Extreme Cold (<-20°C)',
    icon: WEATHER_ICONS.COLD,
    casualty_multiplier: 1.25,
    description: '25% higher casualties - severe cold injuries',
    
    helicopter_availability: 0.35,
    evacuation_speed_modifier: 0.60,
    drone_effectiveness: 0.5,
    
    medical: {
      golden_hour_achievable: 'CRITICAL',
      medevac_status: 'MINIMAL',
      hypothermia_risk: 4.0,
      frostbite_risk: 3.5,
      equipment_failure_rate: 1.5
    }
  },
  
  EXTREME_HEAT: {
    value: 'EXTREME_HEAT',
    label: 'Extreme Heat (>40°C)',
    icon: WEATHER_ICONS.HOT,
    casualty_multiplier: 1.20,
    description: '20% higher casualties - heat injuries',
    
    helicopter_availability: 0.75,
    evacuation_speed_modifier: 0.85,
    drone_effectiveness: 0.9,
    
    medical: {
      golden_hour_achievable: 'DEGRADED',
      medevac_status: 'OPERATIONAL',
      heat_casualty_risk: 3.0,
      dehydration_risk: 2.5
    }
  },
  
  FOG: {
    value: 'FOG',
    label: 'Fog',
    icon: WEATHER_ICONS.FOG,
    casualty_multiplier: 1.10,
    description: '10% higher casualties - reduced visibility',
    
    helicopter_availability: 0.20,
    evacuation_speed_modifier: 0.50,
    drone_effectiveness: 0.4,
    
    medical: {
      golden_hour_achievable: 'SEVERELY_DEGRADED',
      medevac_status: 'GROUNDED',
      ground_evac_only: true
    }
  },
  
  ADVERSE: {
    value: 'ADVERSE',
    label: 'Adverse (Combined)',
    icon: WEATHER_ICONS.STORM,
    casualty_multiplier: 1.30,
    description: '30% higher casualties - multiple adverse factors',
    
    helicopter_availability: 0.15,
    evacuation_speed_modifier: 0.40,
    drone_effectiveness: 0.3,
    
    medical: {
      golden_hour_achievable: 'UNLIKELY',
      medevac_status: 'GROUNDED',
      pfc_required: true,
      ground_evac_only: true
    }
  },

  // 2026-10-20 (R2-16, W1): mud season (rasputitsa), spring or autumn thaw. Before this the mud season existed only in the hidden road-panel
  // weather list. Road effect: locConfig WEATHER_ROUTE_IMPACTS.RASPUTITSA (speed 0.3, unpaved roads nearly impassable) and threatEffects
  // ROUTE_WEATHER_AVAILABILITY.RASPUTITSA (0.5); helicopters, evacuation speed and drones below are EDUCATED GUESS values (low cloud, rain
  // and mud keep aircraft and drones down; wheeled and tracked ambulances move slowly). casualty_multiplier is 1.0 and, as for the other
  // options, not read on the phase-table path.
  RASPUTITSA: {
    value: 'RASPUTITSA',
    label: 'Mud season (rasputitsa)',
    icon: WEATHER_ICONS.RAIN,
    casualty_multiplier: 1.0,
    description: 'Spring or autumn thaw: unpaved roads nearly impassable, slow movement, helicopters and drones limited',

    helicopter_availability: 0.50,
    evacuation_speed_modifier: 0.50,
    drone_effectiveness: 0.6,

    ukraine_data: {
      notes: 'Rasputitsa (Ukraine 2022-2024): mud season limits heavy vehicle movement off paved roads; see locConfig and spatialConfiguration'
    },

    medical: {
      golden_hour_achievable: 'SEVERELY_DEGRADED',
      medevac_status: 'LIMITED',
      hypothermia_risk: 1.5
    }
  }
};

// ============================================================================
// THREAT LEVELS
// ============================================================================

/**
 * THREAT_LEVELS - Enemy capability and intensity
 * Updated with drone warfare considerations
 */
export const THREAT_LEVELS = {
  LOW_THREAT: {
    value: 'LOW_THREAT',
    label: 'Low Threat',
    icon: STATUS_ICONS.GREEN,
    casualty_multiplier: 0.7,
    description: '30% lower casualties - irregular forces, limited capability',
    
    enemy_type: 'Insurgent, militia, limited equipment',
    ad_threat: 'MINIMAL',
    drone_threat: 'MINIMAL',
    
    medical: {
      evacuation_paradigm: 'PERMISSIVE',
      helicopter_risk: 'LOW',
      golden_hour_probability: 0.85,
      first_aid_delay_minutes: 2   // EXT-06: low threat = quick access, minimal suppression wait
    }
  },
  
  MODERATE_THREAT: {
    value: 'MODERATE_THREAT',
    label: 'Moderate Threat',
    icon: STATUS_ICONS.YELLOW,
    casualty_multiplier: 1.0,
    description: 'Baseline threat level',
    
    enemy_type: 'Conventional force, moderate equipment',
    ad_threat: 'MODERATE',
    drone_threat: 'MODERATE',
    
    medical: {
      evacuation_paradigm: 'CONTESTED',
      helicopter_risk: 'MODERATE',
      golden_hour_probability: 0.55,
      first_aid_delay_minutes: 5   // EXT-06: must wait for fire suppression before access
    }
  },
  
  HIGH_THREAT: {
    value: 'HIGH_THREAT',
    label: 'High Threat',
    icon: STATUS_ICONS.YELLOW_WARNING,
    casualty_multiplier: 1.3,
    description: '30% higher casualties - capable adversary',
    
    enemy_type: 'Professional force, combined arms',
    ad_threat: 'HIGH',
    drone_threat: 'HIGH',
    
    ukraine_data: {
      drone_attacks_per_day: '5-10 vehicles typical sector',
      notes: 'Armor and logistics highly vulnerable'
    },
    
    medical: {
      evacuation_paradigm: 'CONTESTED',
      helicopter_risk: 'HIGH',
      golden_hour_probability: 0.35,
      armored_ambulance_required: true,
      first_aid_delay_minutes: 10  // EXT-06: active fire, must stay down, intermittent access
    }
  },
  
  EXTREME_THREAT: {
    value: 'EXTREME_THREAT',
    label: 'Extreme Threat',
    icon: STATUS_ICONS.RED,
    casualty_multiplier: 1.6,
    description: '60% higher casualties - peer adversary',
    
    enemy_type: 'Peer adversary, air defense umbrella, electronic warfare',
    ad_threat: 'EXTREME',
    drone_threat: 'EXTREME',
    
    ukraine_data: {
      examples: ['Bakhmut', 'Avdiivka'],
      notes: 'Vehicles unusable in daylight without EW',
      helicopter_loss_rate: '5% per mission'
    },
    
    medical: {
      evacuation_paradigm: 'DENIED',
      helicopter_risk: 'EXTREME',
      golden_hour_probability: 0.15,
      armored_ambulance_required: true,
      night_evac_only: true,
      pfc_mandatory: true,
      first_aid_delay_minutes: 20  // EXT-06: peer adversary continuous fires, denied access, Bakhmut/Avdiivka pattern
    }
  }
};

// ============================================================================
// DRONE THREAT LEVELS (NEW - Ukraine 2022-2024)
// ============================================================================

/**
 * DRONE_THREAT_LEVELS - Specific drone threat modifiers
 * Critical addition from Ukraine experience
 */
export const DRONE_THREAT_LEVELS = {
  MINIMAL: {
    value: 'MINIMAL',
    label: 'Minimal Drone Threat',
    icon: ICONS.TRANSPORT.DRONE,
    overall_modifier: 1.0,
    armor_modifier: 1.0,
    artillery_modifier: 1.0,
    logistics_modifier: 1.0,
    description: 'No significant drone presence'
  },
  
  LOW: {
    value: 'LOW',
    label: 'Low Drone Threat',
    icon: ICONS.TRANSPORT.DRONE,
    overall_modifier: 1.05,
    armor_modifier: 1.10,
    artillery_modifier: 1.10,
    logistics_modifier: 1.05,
    description: 'Occasional ISR drones, rare FPV'
  },
  
  MODERATE: {
    value: 'MODERATE',
    label: 'Moderate Drone Threat',
    icon: ICONS.TRANSPORT.DRONE,
    overall_modifier: 1.10,
    armor_modifier: 1.25,
    artillery_modifier: 1.20,
    logistics_modifier: 1.15,
    description: 'Regular drone presence, FPV attacks',
    
    ukraine_notes: 'Typical for sectors 20-50km from active combat'
  },
  
  HIGH: {
    value: 'HIGH',
    label: 'High Drone Threat',
    icon: ICONS.TRANSPORT.DRONE,
    overall_modifier: 1.20,
    armor_modifier: 1.50,
    artillery_modifier: 1.40,
    logistics_modifier: 1.30,
    description: 'Constant drone surveillance, frequent FPV attacks',
    
    ukraine_notes: '5-10 vehicles/day lost typical in active sectors'
  },
  
  EXTREME: {
    value: 'EXTREME',
    label: 'Extreme Drone Threat',
    icon: ICONS.TRANSPORT.DRONE,
    overall_modifier: 1.35,
    armor_modifier: 1.80,
    artillery_modifier: 1.60,
    logistics_modifier: 1.50,
    description: 'Drone saturation, vehicles unusable without EW',
    
    ukraine_notes: 'Bakhmut/Avdiivka pattern - daylight movement suicidal'
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate combined casualty modifier from tactical factors
 * @param {Object} config - Configuration object
 * @returns {number} Combined modifier (multiplicative)
 */
export function calculateCombinedModifier(config) {
  const {
    operation = 'GROUND_COMBAT',
    posture = 'ATTACK',
    engagement = 'FULL',
    terrain = 'MIXED',
    weather = 'CLEAR',
    threat = 'MODERATE_THREAT',
    droneLevel = null
  } = config;
  
  let modifier = 1.0;
  
  // Operation type
  modifier *= OPERATION_TYPES[operation]?.casualty_multiplier || 1.0;
  
  // Posture
  modifier *= OPERATIONAL_POSTURES[posture]?.casualty_multiplier || 1.0;
  
  // Engagement level
  modifier *= ENGAGEMENT_LEVELS[engagement]?.casualty_multiplier || 1.0;
  
  // Terrain
  modifier *= TERRAIN_TYPES[terrain]?.casualty_multiplier || 1.0;
  
  // Weather
  modifier *= WEATHER_CONDITIONS[weather]?.casualty_multiplier || 1.0;
  
  // Threat
  modifier *= THREAT_LEVELS[threat]?.casualty_multiplier || 1.0;
  
  // Drone threat (if specified separately)
  if (droneLevel && DRONE_THREAT_LEVELS[droneLevel]) {
    modifier *= DRONE_THREAT_LEVELS[droneLevel].overall_modifier;
  }
  
  return modifier;
}

/**
 * Calculate environmental modifier only
 * @param {string} terrain - Terrain type
 * @param {string} weather - Weather condition
 * @param {string} threat - Threat level
 * @returns {number} Combined environmental modifier
 */
export function calculateEnvironmentalModifier(terrain, weather, threat) {
  const terrainMod = TERRAIN_TYPES[terrain]?.casualty_multiplier || 1.0;
  const weatherMod = WEATHER_CONDITIONS[weather]?.casualty_multiplier || 1.0;
  const threatMod = THREAT_LEVELS[threat]?.casualty_multiplier || 1.0;
  return terrainMod * weatherMod * threatMod;
}

/**
 * Determine evacuation paradigm based on weather and threat
 * @param {string} weather - Weather condition
 * @param {string} threat - Threat level
 * @returns {string} 'PERMISSIVE' | 'CONTESTED' | 'DENIED'
 */
export function determineEvacuationParadigm(weather, threat) {
  const weatherData = WEATHER_CONDITIONS[weather];
  const threatData = THREAT_LEVELS[threat];
  
  if (!weatherData || !threatData) {
    return 'PERMISSIVE';
  }
  
  const heloAvailability = weatherData.helicopter_availability;
  const threatMod = threatData.casualty_multiplier;
  
  // DENIED: Severe weather (<40% helo) + high threat (≥1.3x)
  if (heloAvailability < 0.40 && threatMod >= 1.3) {
    return 'DENIED';
  }
  
  // DENIED: Extreme threat alone
  if (threatMod >= 1.6) {
    return 'DENIED';
  }
  
  // CONTESTED: Moderate weather (<70% helo) OR high threat (≥1.3x)
  if (heloAvailability < 0.70 || threatMod >= 1.3) {
    return 'CONTESTED';
  }
  
  // PERMISSIVE: Good weather (≥70% helo) + low-moderate threat (<1.3x)
  return 'PERMISSIVE';
}

/**
 * Get golden hour probability
 * @param {string} weather - Weather condition
 * @param {string} threat - Threat level
 * @param {string} posture - Operational posture
 * @returns {number} Probability 0-1
 */
export function getGoldenHourProbability(weather, threat, posture = 'ATTACK') {
  const paradigm = determineEvacuationParadigm(weather, threat);
  const postureData = OPERATIONAL_POSTURES[posture];
  
  // Base probability by paradigm
  let probability = 0.50;
  if (paradigm === 'PERMISSIVE') probability = 0.75;
  if (paradigm === 'DENIED') probability = 0.15;
  
  // Adjust by posture
  if (postureData?.medical?.golden_hour_probability) {
    probability = (probability + postureData.medical.golden_hour_probability) / 2;
  }
  
  return Math.max(0.05, Math.min(0.95, probability));
}

/**
 * Get weather impact on operations
 * @param {string} weather - Weather condition
 * @returns {Object} Weather impact data
 */
export function getWeatherImpact(weather) {
  const condition = WEATHER_CONDITIONS[weather];
  if (!condition) {
    return { 
      availability: 0.85, 
      speedModifier: 1.0,
      droneEffectiveness: 1.0 
    };
  }
  
  return {
    availability: condition.helicopter_availability,
    speedModifier: condition.evacuation_speed_modifier,
    droneEffectiveness: condition.drone_effectiveness
  };
}

/**
 * Check if operation type is compatible with posture
 * @param {string} operation - Operation type
 * @param {string} posture - Operational posture
 * @returns {boolean} True if compatible
 */
export function isOperationPostureCompatible(operation, posture) {
  const allowedOps = OPERATIONAL_POSTURES[posture]?.allowedOperations || [];
  return allowedOps.includes(operation);
}

/**
 * Get compatible operations for a posture
 * @param {string} posture - Operational posture
 * @returns {Array} Array of compatible operation type keys
 */
export function getCompatibleOperations(posture) {
  return OPERATIONAL_POSTURES[posture]?.allowedOperations || [];
}

/**
 * Get drone warfare modifier for unit type
 * @param {string} droneLevel - Drone threat level
 * @param {string} unitCategory - ARMOR, ARTILLERY, LOGISTICS, OTHER
 * @returns {number} Drone modifier
 */
export function getDroneModifier(droneLevel, unitCategory = 'OTHER') {
  const drone = DRONE_THREAT_LEVELS[droneLevel];
  if (!drone) return 1.0;
  
  switch (unitCategory) {
    case 'ARMOR':
      return drone.armor_modifier;
    case 'ARTILLERY':
      return drone.artillery_modifier;
    case 'LOGISTICS':
      return drone.logistics_modifier;
    default:
      return drone.overall_modifier;
  }
}

/**
 * Get all options for dropdowns
 */
export function getOperationTypesArray() {
  return Object.values(OPERATION_TYPES).map(o => ({
    value: o.value,
    label: o.label,
    icon: o.icon,
    casualty_multiplier: o.casualty_multiplier
  }));
}

export function getPosturesArray() {
  return Object.values(OPERATIONAL_POSTURES).map(p => ({
    value: p.value,
    label: p.label,
    icon: p.icon,
    casualty_multiplier: p.casualty_multiplier
  }));
}

export function getEngagementLevelsArray() {
  return Object.values(ENGAGEMENT_LEVELS).map(e => ({
    value: e.value,
    label: e.label,
    icon: e.icon,
    casualty_multiplier: e.casualty_multiplier
  }));
}

export function getTerrainTypesArray() {
  return Object.values(TERRAIN_TYPES).map(t => ({
    value: t.value,
    label: t.label,
    icon: t.icon,
    casualty_multiplier: t.casualty_multiplier
  }));
}

export function getWeatherConditionsArray() {
  return Object.values(WEATHER_CONDITIONS).map(w => ({
    value: w.value,
    label: w.label,
    icon: w.icon,
    casualty_multiplier: w.casualty_multiplier,
    helicopter_availability: w.helicopter_availability
  }));
}

export function getThreatLevelsArray() {
  return Object.values(THREAT_LEVELS).map(t => ({
    value: t.value,
    label: t.label,
    icon: t.icon,
    casualty_multiplier: t.casualty_multiplier
  }));
}

// ============================================================================
// EDUCATIONAL CONTENT
// ============================================================================

// ============================================================================
// CLS_TRAINING_QUALITY
// EXT-06: Combat Lifesaver training quality levels with survival modifiers
// and TXA probability. Consumed by survivalModule.js — replaces its local
// CLS_TRAINING_QUALITY block (SVM-02, Phase 4).
//
// survival_modifier: multiplier applied to pre-hospital survival probability.
//   Derived from TCCC efficacy research: full TCCC protocol reduces preventable
//   prehospital deaths by ~30% (tourniquet = 20%, TXA = 10-15%, NPA = 5%).
//   UNTRAINED=1.00 (baseline), ADVANCED=1.30 (30% improvement).
//
// txa_probability: probability that this CLS level has TXA available and
//   administers it correctly within the therapeutic window (≤3 hours).
//   Ukraine 2022-2024: TXA administration highly correlated with training level.
//
// References: TCCC Guidelines (COTCCC 2024); CRASH-2 trial;
//   Roberts et al. TXA systematic review; Ukraine 2022-2024 outcomes data;
//   USSOCOM Combat Casualty Care Research
// ============================================================================

export const CLS_TRAINING_QUALITY = {
  UNTRAINED: {
    value: 'UNTRAINED',
    label: 'Untrained',
    description: 'No formal first aid training. Instinct response only.',
    survival_modifier: 1.00,  // baseline — no trained intervention
    txa_probability:  0.00,   // no TXA knowledge or supply
    tourniquet_correct_rate: 0.20, // accidental correct application only
    notes: 'Worst case. Use for conscript units without training pipeline.'
  },

  BASIC: {
    value: 'BASIC',
    label: 'Basic First Aid',
    description: 'Basic first aid training only. Tourniquet and pressure dressing application.',
    survival_modifier: 1.08,  // 8% improvement — correct tourniquet reduces extremity deaths
    txa_probability:  0.10,   // TXA rarely available or correctly administered
    tourniquet_correct_rate: 0.65,
    notes: 'Minimum acceptable standard for conventional forces.'
  },

  STANDARD: {
    value: 'STANDARD',
    label: 'Standard TCCC (NATO)',
    description: 'NATO TCCC training. Tourniquet, TXA, needle decompression, hemostatic dressings.',
    survival_modifier: 1.18,  // 18% improvement — full TCCC kit + training
    txa_probability:  0.40,   // TXA in kit; ~40% administered correctly in time
    tourniquet_correct_rate: 0.88,
    notes: 'NATO standard. Validated by multiple conflict analyses.'
  },

  ADVANCED: {
    value: 'ADVANCED',
    label: 'Advanced TCCC',
    description: 'Advanced TCCC + enhanced protocols. IO access, airway management, extended PFC.',
    survival_modifier: 1.30,  // 30% improvement — ceiling of prehospital intervention benefit
    txa_probability:  0.75,   // high TXA compliance with refresher training
    tourniquet_correct_rate: 0.96,
    notes: 'SOF/ranger standard. Ukraine data shows ~30% survival improvement possible.'
  }
};

// ============================================================================
// OPERATIONS EDUCATIONAL
// ============================================================================

export const OPERATIONS_EDUCATIONAL = {
  key_principles: [
    'Combined modifiers are multiplicative (e.g., 1.4 × 1.2 × 1.3 = 2.18x)',
    'Weather affects helicopter availability - critical for golden hour',
    'Drone threat is now a primary modifier in modern warfare',
    'Posture + operation + terrain combined determine casualty rate'
  ],
  
  ukraine_lessons: [
    'Hasty attacks without ISR are catastrophic (1.35x or higher)',
    'Fortified defense reduces casualties by 50%+ (0.5x)',
    'River crossings without air superiority near suicidal (1.8x)',
    'Rain/fog significantly degrade drone effectiveness (0.4-0.6x)',
    'Extreme drone threat makes daylight armor movement impossible'
  ],
  
  medical_planning: [
    'PERMISSIVE: 85% helicopter availability, 75% golden hour',
    'CONTESTED: 40-70% helicopter, 35-55% golden hour',
    'DENIED: <40% helicopter, 15% golden hour, PFC mandatory',
    'Weather directly impacts evacuation paradigm'
  ],
  
  common_errors: [
    'Ignoring drone threat in modern calculations',
    'Assuming golden hour always achievable',
    'Not adjusting for weather impact on MEDEVAC',
    'Using historical modifiers without Ukraine validation'
  ]
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

// Named default export (fixes lint warning)

// ============================================================================
// MISSION ROLES
// BCM-10: Moved from battleCasualtyModule.js — single source of truth for
// brigade-level mission role assignment and casualty multipliers.
// In division-level operations, brigades are assigned different roles that
// determine their casualty exposure and priority of support.
// References: NATO ATP 3-90 Offense and Defense, FM 3-90-1 Offense,
//   Ukraine 2022-2024 data (ISW, RUSI analysis)
// Educational: This is why division attacks with "two up, one back" —
//   the reserve brigade takes minimal casualties while maintaining flexibility.
// ============================================================================

export const MISSION_ROLES = {
  MAIN_EFFORT: {
    value: 'MAIN_EFFORT',
    label: 'Main Effort',
    casualty_multiplier: 1.5,
    description: 'Primary attacking force - receives priority but bears highest casualties',
    typical_strength_allocation: 0.40, // 40% of division strength
    fire_support_priority: 1,
    ukraine_data: {
      examples: ['Kherson offensive main axis', 'Bakhmut assault brigades'],
      observed_multiplier: 1.4 - 1.8,
      notes: 'Main effort consistently 40-80% higher casualties than supporting'
    },
    romanian_term: 'Efort Principal'
  },

  SUPPORTING: {
    value: 'SUPPORTING',
    label: 'Supporting',
    casualty_multiplier: 1.0,
    description: 'Supports main effort - fixes enemy, provides fires, or secures flanks',
    typical_strength_allocation: 0.35, // 35% of division strength
    fire_support_priority: 2,
    ukraine_data: {
      examples: ['Flank security during Kharkiv offensive'],
      observed_multiplier: 0.8 - 1.2,
      notes: 'Supporting efforts may become main effort if opportunity arises'
    },
    romanian_term: 'Sprijin'
  },

  RESERVE: {
    value: 'RESERVE',
    label: 'Reserve',
    casualty_multiplier: 0.2,
    description: 'Held in reserve - minimal contact until committed',
    typical_strength_allocation: 0.25, // 25% of division strength
    fire_support_priority: 3,
    ukraine_data: {
      examples: ['Ukrainian strategic reserve during Kherson'],
      observed_multiplier: 0.1 - 0.3,
      notes: 'Reserve takes almost no casualties until committed, then becomes main/supporting'
    },
    warning: 'Once committed, change mission_role to MAIN_EFFORT or SUPPORTING',
    romanian_term: 'Rezervă'
  },

  EXPLOITATION: {
    value: 'EXPLOITATION',
    label: 'Exploitation',
    casualty_multiplier: 1.3,
    description: 'Exploiting breakthrough - rapid advance, increased exposure',
    typical_strength_allocation: 'variable', // Committed from reserve
    fire_support_priority: 1, // Gets priority during exploitation
    ukraine_data: {
      examples: ['Kharkiv offensive rapid advance September 2022'],
      observed_multiplier: 0.7 - 1.5,
      notes: 'Against routed enemy: 0.7x. Against organized defense: 1.5x',
      contradiction: 'Historical doctrine says 1.3x assumes resistance; Ukraine showed variable'
    },
    romanian_term: 'Exploatare'
  },

  ECONOMY_OF_FORCE: {
    value: 'ECONOMY_OF_FORCE',
    label: 'Economy of Force',
    casualty_multiplier: 0.6,
    description: 'Minimal force to hold secondary sector - accepts risk',
    typical_strength_allocation: 0.15, // Minimum viable force
    fire_support_priority: 4, // Lowest priority
    ukraine_data: {
      examples: ['Ukrainian forces holding Bakhmut flanks with minimal strength'],
      observed_multiplier: 0.5 - 0.8,
      notes: 'Economy of force accepts risk of enemy breakthrough in sector'
    },
    romanian_term: 'Economie de Forțe'
  },

  // Additional roles for completeness
  FEINT: {
    value: 'FEINT',
    label: 'Feint/Demonstration',
    casualty_multiplier: 0.4,
    description: 'Deception operation - appears to attack but avoids decisive engagement',
    fire_support_priority: 3,
    romanian_term: 'Fentă'
  },

  FIXING: {
    value: 'FIXING',
    label: 'Fixing Force',
    casualty_multiplier: 0.8,
    description: 'Pins enemy in place while main effort maneuvers',
    fire_support_priority: 2,
    romanian_term: 'Forță de Fixare'
  }
};

/**
 * Get mission role casualty multiplier
 * BCM-10: Moved from battleCasualtyModule.js.
 *
 * @param {string} missionRole - Mission role key (MAIN_EFFORT, SUPPORTING, RESERVE, etc.)
 * @returns {number} Casualty multiplier for the mission role (1.0 default)
 */
export function getMissionRoleMultiplier(missionRole) {
  const role = MISSION_ROLES[missionRole];
  if (role) {
    return role.casualty_multiplier;
  }
  // Default to SUPPORTING (baseline 1.0) if unknown role
  return 1.0;
}


// ============================================================================
// GROUP-1-D: MEDICAL STRUCTURE PROTECTION LEVEL (Gielerak & Murawski 2024)
// ============================================================================
// Source: Gielerak GG, Murawski P. "A Model of Dynamic Planning of Medical Support
//         for Combat Troops." Lekarz Wojskowy 2024, Vol. 102, Nr 1. DOI: 10.53301/lw/177410
//
// The Gielerak model defines the operational safety coefficient `bo` (0-1):
//   wss = 1 + (1 - bo) x sso x ps
// where wss = per-level sanitary losses, sso = loss intensity, ps = personnel strength.
// A lower `bo` means medical structures are MORE exposed to enemy action (targeting,
// drone strikes, artillery), resulting in higher attrition of medical personnel and
// therefore reduced surgical capacity for all casualties.
//
// In PSP/CRE this is expressed as a percentage (0-100%) mapped to a surgical
// capacity factor via: _boCapMod = BO_FACTOR_MIN + (bo / 100) x (1 - BO_FACTOR_MIN)
//   bo = 100% -> _boCapMod = 1.00  (full capacity, fully protected structures)
//   bo =  70% -> _boCapMod = 0.85  (15% surgical degradation, default LSCO posture)
//   bo =  30% -> _boCapMod = 0.65  (35% degradation, contested medical space)
//   bo =   0% -> _boCapMod = 0.50  (50% degradation, medical structures fully exposed)
//
// This modifier stacks multiplicatively with the Class VIII DOS degradation factor
// (_class8CapMod) and the Monte Carlo surgical capacity modifier (_mcSurgCapMod).
// ============================================================================

/** Default medical protection level (%) — AJMedP-1 planning assumption: contested LSCO */
export const MEDICAL_PROTECTION_DEFAULT = 70;

/** Minimum surgical capacity factor at 0% protection (fully exposed medical structures) */
export const MEDICAL_PROTECTION_BO_FACTOR_MIN = 0.5;

/**
 * HICOM-001: DOW multiplier when Role 3 is inaccessible.
 * Without definitive surgical capability at R3, borderline non-survivable WIA
 * convert to KIA at higher rates. Source: doctrine-derived; same order of magnitude
 * as CASEVAC DOW penalty (1.35). V3 = warning display only. V4 = dual calculation.
 */
export const R3_ABSENT_DOW_MODIFIER = 1.35;

/**
 * Color-coded thresholds for UI display of protection level.
 * Aligned with NATO MASCAL traffic-light convention (green/amber/red).
 *   >= HIGH   -> green  (adequate protection, within doctrinal norms)
 *   >= LOW    -> amber  (degraded — heightened medical targeting risk)
 *   <  LOW    -> red    (critical — medical structures largely unprotected)
 */
export const MEDICAL_PROTECTION_THRESHOLDS = {
  HIGH: 60,   // >= 60% = green
  LOW:  30    // >= 30% = amber; < 30% = red
};

const operationsConfigConstants = {
  OPERATION_TYPES,
  OPERATIONAL_POSTURES,
  ENGAGEMENT_LEVELS,
  TERRAIN_TYPES,
  WEATHER_CONDITIONS,
  THREAT_LEVELS,
  DRONE_THREAT_LEVELS,
  CLS_TRAINING_QUALITY,
  calculateCombinedModifier,
  calculateEnvironmentalModifier,
  determineEvacuationParadigm,
  getGoldenHourProbability,
  getWeatherImpact,
  isOperationPostureCompatible,
  getCompatibleOperations,
  getDroneModifier,
  getOperationTypesArray,
  getPosturesArray,
  getEngagementLevelsArray,
  getTerrainTypesArray,
  getWeatherConditionsArray,
  getThreatLevelsArray,
  OPERATIONS_EDUCATIONAL,
  MISSION_ROLES,
  getMissionRoleMultiplier,
  MEDICAL_PROTECTION_DEFAULT,
  MEDICAL_PROTECTION_BO_FACTOR_MIN,
  MEDICAL_PROTECTION_THRESHOLDS
};
export default operationsConfigConstants;
